// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Description,
  H1,
  LgPadding,
  LgSpacing,
  ListBase,
  ListDescription,
  ListItem,
  MdSpacing,
  Separator,
  XsSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import pluralize from 'pluralize';
import React, { useCallback, useEffect } from 'react';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import {
  getProgynyWizardCycle,
  getProgynyWizardFirstFill,
  getProgynyWizardUpdateAttributes,
} from '~shared/selectors/checkout/progyny';
import {
  QUANTITY_REVIEW_BLOCKERS,
  QUANTITY_REVIEW_STATUS,
  getExceededDaysSupplyBlockerMeds,
  getQuantityReviewStatus,
} from '~shared/selectors/progyny-wizard/getQuantityReviewStatus';
import { useSelectorShared } from '~shared/store';
import { type Prescription } from '~shared/types';
import { useNavigateToAssistantLanding } from '../../alto-assistant';
import { CHECKOUT_STEPS, useCheckoutInterstitials, useProgynyWizard } from '../../checkout';
import { MedImage } from '../../my-meds';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { QUANTITIES_REVIEW_STRINGS } from '../constants';
import { useUpdateFertilityCycle } from '../hooks/useUpdateFertilityCycle';
import QuantitiesReviewAlert from './QuantitiesReviewAlert';

export const QuantitiesReview = () => {
  const { navigate, goBack } = useNavigation();
  const { trackPageView } = useAnalytics();
  const quantityReviewStatus = useSelectorShared(getQuantityReviewStatus);
  const blockerMeds = useSelectorShared(getExceededDaysSupplyBlockerMeds);
  const updateAttributesTriggerDate = useSelectorShared(getProgynyWizardUpdateAttributes)?.trigger_date;
  const wizardCycleTriggerDate = useSelectorShared(getProgynyWizardCycle)?.trigger_date;
  const triggerDate = updateAttributesTriggerDate ?? wizardCycleTriggerDate ?? null;
  const isFirstFill = useSelectorShared(getProgynyWizardFirstFill);
  const isRefill = !isFirstFill;
  const completeCheckoutStep = useCheckoutInterstitials();
  const { navigateToAssistantLanding } = useNavigateToAssistantLanding();
  const { eligiblePrescriptionsInCart: prescriptions } = useProgynyWizard();
  const blockContinue = quantityReviewStatus ? QUANTITY_REVIEW_BLOCKERS.includes(quantityReviewStatus) : false;
  const showItems = quantityReviewStatus
    ? ![QUANTITY_REVIEW_STATUS.blocker_trigger_date].includes(quantityReviewStatus)
    : true;
  const { updateFertilityCycle, isPending: updateFertilityCycleLoading } = useUpdateFertilityCycle();

  const onContactUs = useCallback(() => {
    navigateToAssistantLanding();
  }, [navigateToAssistantLanding]);

  const onBack = useCallback(() => {
    navigate('RouteCart');
  }, [navigate]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.MEDICATION_QUANTITIES_VIEWED,
      params: {
        is_refill: isRefill,
      },
    });
  }, [isRefill, trackPageView]);

  const handlePress = useCallback(async () => {
    const updatedFertilityCycle = await updateFertilityCycle();
    if (updatedFertilityCycle) await completeCheckoutStep(CHECKOUT_STEPS.PROGYNY_WIZARD);
  }, [completeCheckoutStep, updateFertilityCycle]);

  const formatQuantity = (prescription: Prescription) => {
    const { days_supply, dosage_form, quantity } = prescription;

    if (!quantity) {
      return '';
    }

    const formatted_dosage_form = dosage_form ? ` ${pluralize(dosage_form, quantity)}` : '';
    const formatted_days_supply = days_supply ? ` (${days_supply} day supply)` : '';
    return `Qty: ${quantity}${formatted_dosage_form}${formatted_days_supply}`;
  };

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      HeaderContent={
        <LgPadding topPadding={SPACING.STATIC.MD}>
          <MdSpacing />
          <H1>Your personalized order</H1>
          <XsSpacing />
          <Body>
            Here’s your optimized ProgynyRx medication order — all based on <Body fontFamily="semibold">exactly</Body>{' '}
            where you are in your treatment cycle.
          </Body>
        </LgPadding>
      }
      Footnote={<Description center>Ordering other medications? Review your full order in checkout.</Description>}
      buttons={
        blockContinue
          ? [
              <Button
                key={QUANTITIES_REVIEW_STRINGS.CONTACT_US_BUTTON_TEXT}
                label={QUANTITIES_REVIEW_STRINGS.CONTACT_US_BUTTON_TEXT}
                onPress={onContactUs}
              />,
              <Button
                key={QUANTITIES_REVIEW_STRINGS.BACK_BUTTON_TEXT}
                label={QUANTITIES_REVIEW_STRINGS.BACK_BUTTON_TEXT}
                onPress={onBack}
                type="secondary"
              />,
            ]
          : [
              <Button
                key="Continue to checkout"
                label="Continue to checkout"
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPress={handlePress}
                loading={updateFertilityCycleLoading}
                testID="e2e-progyny-continue-to-checkout-button"
              />,
            ]
      }
    >
      {quantityReviewStatus ? (
        <>
          <QuantitiesReviewAlert
            quantityReviewStatus={quantityReviewStatus}
            blockerMeds={blockerMeds}
            triggerDate={triggerDate}
          />
          <LgSpacing />
        </>
      ) : null}
      <Card>
        {showItems ? (
          <ListBase
            separator={<Separator />}
            items={prescriptions.map((prescription) => ({
              key: prescription.id,
              component: (
                <ListItem
                  backgroundColor={
                    blockerMeds[prescription.id.toString()]
                      ? COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST
                      : COLORS.BACKGROUND_COLORS.WHITE
                  }
                  LeftContent={
                    <MedImage
                      imageUrl={prescription.image_url}
                      clickable={false}
                    />
                  }
                  title={`${prescription.medication_name}`}
                  descriptions={[
                    <ListDescription key={`${prescription.medication_name}-quantity`}>
                      {formatQuantity(prescription)}
                    </ListDescription>,
                  ]}
                />
              ),
            }))}
          />
        ) : null}
      </Card>
    </ProgynyPage>
  );
};
